import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  makeStyles,
  TextareaAutosize,
  Typography,
} from "@material-ui/core";
import React from "react";
import { useFeedbackService } from "../../api/ServiceContext";
import { colors } from "../../colors";
import {
  useSelectedMachine,
  useUserPreferencesActions,
} from "../../store/GlobalContext";
import { askForFeedbackKey } from "../../utility/LocalStorageKeys";
import { useDispatchAlert } from "../UI/Alert";

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  paper: {
    backgroundColor: colors.mobaBlue,
    borderRadius: 12,
    color: "#ffffff",
    padding: 0,
    maxWidth: 420,
    width: "100%",
    boxShadow: "0 8px 24px rgba(2,19,55,0.6)",
  },
  headerStrip: {
    backgroundColor: colors.mobaBlue,
    height: 56,
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    position: "relative",
  },
  logoPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: colors.mobaOrange,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontWeight: 700,
    marginRight: 12,
  },
  titleText: {
    color: "#ffffff",
    fontWeight: 600,
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1,
  },
  closeButton: {
    backgroundColor: colors.priorityRed,
    color: "#ffffff",
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "50%",
    boxShadow: "0 2px 6px rgba(230,0,0,0.25)",
    marginLeft: 8,
    "&:hover": {
      backgroundColor: "#BF0000",
    },
  },
  headerCloseContainer: {
    position: "absolute",
    right: 12,
    top: 10,
    zIndex: 2,
  },
  ratingRow: {
    display: "flex",
    justifyContent: "center",
    gap: 16,
    padding: "20px 24px",
  },
  ratingIcon: {
    width: 56,
    height: 56,
    padding: 4,
    boxSizing: "border-box",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: "none",
    color: "#ffffff",
    cursor: "pointer",
    transition:
      "transform 150ms ease, background-color 150ms ease, box-shadow 150ms ease",
  },
  selectedRating: {
    backgroundColor: colors.lightBlue,
    transform: "scale(1.06)",
    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
  },
  content: {
    padding: "8px 20px 16px 20px",
  },
  labelText: {
    color: "rgba(255,255,255,0.9)",
    marginBottom: 8,
    fontSize: 14,
  },
  contactLabel: {
    color: "rgba(255,255,255,0.9)",
    fontSize: 14,
  },
  contactRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
  },
  textarea: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.08)",
    padding: 12,
    color: "#272727",
    width: "100%",
    boxSizing: "border-box",
    minHeight: 120,
    marginBottom: 12,
    resize: "vertical",
  },
  actions: {
    padding: "12px 20px 20px 20px",
    display: "flex",
    justifyContent: "center",
    gap: 20,
  },
  leftActions: {
    display: "flex",
    gap: 20,
  },
  primaryButton: {
    backgroundColor: colors.lightBlue,
    color: "#ffffff",
    borderRadius: 6,
    padding: "8px 18px",
    marginRight: 12,
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#0077e6",
    },
  },
  secondaryButton: {
    backgroundColor: colors.lightBlue,
    color: "#ffffff",
    borderRadius: 6,
    padding: "8px 18px",
    marginLeft: 12,
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#0077e6",
    },
  },
  textareaContainer: {
    position: "relative" as const,
    marginBottom: 12,
  },
  characterCount: {
    color: "rgba(255,255,255,0.7)",
    fontSize: 12,
    textAlign: "right" as const,
    marginTop: 4,
  },
  characterCountWarning: {
    color: colors.mobaOrange,
  },
  characterCountError: {
    color: colors.priorityRed,
  },
  requiredLabel: {
    color: "rgba(255,255,255,0.9)",
    marginBottom: 8,
    fontSize: 14,
  },
  requiredAsterisk: {
    color: colors.priorityRed,
    marginLeft: 4,
  },
  ratingError: {
    color: colors.priorityRed,
    fontSize: 12,
    textAlign: "center" as const,
    marginTop: -12,
    marginBottom: 8,
  },
}));

const MAX_CHARACTERS = 2000;

export default function PerformanceProFeedbackDialog({ open, onClose }: Props) {
  const classes = useStyles();
  const feedbackService = useFeedbackService();
  const { user } = useAuth0();
  const machineId = useSelectedMachine();
  const dispatchAlert = useDispatchAlert();
  const { updateAskForFeedback } = useUserPreferencesActions();

  const [rating, setRating] = React.useState<number | null>(null);
  const [feedbackText, setFeedbackText] = React.useState("");
  const [improvementText, setImprovementText] = React.useState("");
  const [allowContact, setAllowContact] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showRatingError, setShowRatingError] = React.useState(false);

  const isFormValid = rating !== null;

  const getCharacterCountClass = (length: number) => {
    if (length >= MAX_CHARACTERS)
      return `${classes.characterCount} ${classes.characterCountError}`;
    if (length >= MAX_CHARACTERS * 0.9)
      return `${classes.characterCount} ${classes.characterCountWarning}`;
    return classes.characterCount;
  };

  const handleSend = async () => {
    if (!isFormValid) {
      setShowRatingError(true);
      return;
    }
    setShowRatingError(false);
    setIsSubmitting(true);
    try {
      await feedbackService.sendFeedback({
        email: user.name,
        starRating: rating ?? 0,
        RatingSubstantiation: feedbackText,
        improvementSuggestion: improvementText,
        allowContact: allowContact,
        machineId: machineId.machineId,
      });
      localStorage.setItem(askForFeedbackKey, "false");
      updateAskForFeedback(false);

      // Clear form fields
      setRating(null);
      setFeedbackText("");
      setImprovementText("");
      setAllowContact(false);

      dispatchAlert({
        message: "Thank you for your feedback.",
        messageType: "success",
      });
    } catch {
      dispatchAlert({
        message:
          "Failed to send feedback. It is always possible to give feedback by using the Feedback button in the top right corner.",
        messageType: "error",
      });
    }
    onClose();
    setIsSubmitting(false);
  };

  const handleClose = async () => {
    setIsSubmitting(true);
    try {
      await feedbackService.askLater({
        email: user.name,
      });
      localStorage.setItem(askForFeedbackKey, "false");
      updateAskForFeedback(false);

      dispatchAlert({
        message:
          "It is always possible to give feedback by using the Feedback button in the top right corner.",
        messageType: "info",
      });
    } catch {
      dispatchAlert({
        message:
          "An error occurred. It is always possible to give feedback by using the Feedback button in the top right corner.",
        messageType: "error",
      });
    }

    // Clear form fields
    setRating(null);
    setFeedbackText("");
    setImprovementText("");
    setAllowContact(false);
    setShowRatingError(false);

    onClose();
    setIsSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="feedback-dialog"
      PaperProps={{ className: classes.paper }}
      maxWidth={false}
      style={{ zIndex: 2100 }}
    >
      <div className={classes.headerStrip}>
        <Typography className={classes.titleText} variant="h6">
          We value your opinion
        </Typography>
        <div className={classes.headerCloseContainer}>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ×
          </IconButton>
        </div>
      </div>
      <DialogContent className={classes.content}>
        <Typography className={classes.requiredLabel}>
          How would you rate iMoba?<span className={classes.requiredAsterisk}>*</span>
        </Typography>
        <div className={classes.ratingRow}>
          {[1, 2, 3, 4, 5].map((r) => (
            <button
              key={r}
              type="button"
              aria-label={`rating-${r}`}
              className={`${classes.ratingIcon} ${
                rating === r ? classes.selectedRating : ""
              }`}
              onClick={() => {
                setRating(r);
                setShowRatingError(false);
              }}
            >
              {r === 1 && (
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 200 260"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 40 C145 40 180 115 180 165 C180 225 140 260 100 260 C60 260 20 225 20 165 C20 115 55 40 100 40 Z"
                    fill="#e6463f"
                  />
                  <circle cx="75" cy="135" r="7" fill="#000" />
                  <circle cx="125" cy="135" r="7" fill="#000" />
                  <path
                    d="M70 190 Q100 160 130 190"
                    fill="none"
                    stroke="#000"
                    strokeWidth={5}
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {r === 2 && (
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 200 260"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 40 C145 40 180 115 180 165 C180 225 140 260 100 260 C60 260 20 225 20 165 C20 115 55 40 100 40 Z"
                    fill="#ff8c29"
                  />
                  <circle cx="75" cy="135" r="7" fill="#000" />
                  <circle cx="125" cy="135" r="7" fill="#000" />
                  <path
                    d="M70 175 Q100 160 130 175"
                    fill="none"
                    stroke="#000"
                    strokeWidth={5}
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {r === 3 && (
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 200 260"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 40 C145 40 180 115 180 165 C180 225 140 260 100 260 C60 260 20 225 20 165 C20 115 55 40 100 40 Z"
                    fill="#ffd93b"
                  />
                  <circle cx="75" cy="135" r="7" fill="#000" />
                  <circle cx="125" cy="135" r="7" fill="#000" />
                  <line
                    x1="70"
                    y1="175"
                    x2="130"
                    y2="175"
                    stroke="#000"
                    strokeWidth={5}
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {r === 4 && (
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 200 260"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 40 C145 40 180 115 180 165 C180 225 140 260 100 260 C60 260 20 225 20 165 C20 115 55 40 100 40 Z"
                    fill="#9de678"
                  />
                  <circle cx="75" cy="135" r="7" fill="#000" />
                  <circle cx="125" cy="135" r="7" fill="#000" />
                  <path
                    d="M70 165 Q100 195 130 165"
                    fill="none"
                    stroke="#000"
                    strokeWidth={5}
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {r === 5 && (
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 200 260"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 40 C145 40 180 115 180 165 C180 225 140 260 100 260 C60 260 20 225 20 165 C20 115 55 40 100 40 Z"
                    fill="#3fcf3f"
                  />
                  <circle cx="70" cy="130" r="9" fill="#000" />
                  <circle cx="130" cy="130" r="9" fill="#000" />
                  <path
                    d="M60 160 Q100 210 140 160"
                    fill="none"
                    stroke="#000"
                    strokeWidth={7}
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
        {showRatingError && (
          <Typography className={classes.ratingError}>
            Please select a rating before submitting
          </Typography>
        )}

        <div>
          <Typography className={classes.labelText}>
            Let us know why you have given us this rating:
          </Typography>
          <div className={classes.textareaContainer}>
            <TextareaAutosize
              aria-label="feedback-text"
              className={classes.textarea}
              value={feedbackText}
              onChange={(e) =>
                setFeedbackText(e.target.value.slice(0, MAX_CHARACTERS))
              }
              placeholder="It is allowed to give feedback in your own language"
              maxLength={MAX_CHARACTERS}
            />
            <Typography className={getCharacterCountClass(feedbackText.length)}>
              {feedbackText.length} / {MAX_CHARACTERS}
            </Typography>
          </div>
        </div>

        <div>
          <Typography className={classes.labelText}>
            What would make iMoba better for your job?
          </Typography>
          <div className={classes.textareaContainer}>
            <TextareaAutosize
              aria-label="improvement-text"
              className={classes.textarea}
              value={improvementText}
              onChange={(e) =>
                setImprovementText(e.target.value.slice(0, MAX_CHARACTERS))
              }
              placeholder="It is allowed to give feedback in your own language"
              maxLength={MAX_CHARACTERS}
            />
            <Typography
              className={getCharacterCountClass(improvementText.length)}
            >
              {improvementText.length} / {MAX_CHARACTERS}
            </Typography>
          </div>
        </div>

        <div className={classes.contactRow}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allowContact}
                onChange={(e) => setAllowContact(e.target.checked)}
                style={{ color: "#ffffff" }}
                inputProps={{ "aria-label": "allow-contact-checkbox" }}
              />
            }
            classes={{ label: classes.contactLabel }}
            label="Can we contact you if we have any questions on your feedback?"
          />
        </div>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          onClick={handleSend}
          className={classes.primaryButton}
          variant="contained"
          disabled={isSubmitting}
        >
          Send Feedback
        </Button>
        <Button
          onClick={handleClose}
          className={classes.secondaryButton}
          variant="contained"
          disabled={isSubmitting}
        >
          Ask me later
        </Button>
      </DialogActions>
    </Dialog>
  );
}
